import React, { useState, useEffect, useContext, useCallback } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Avatar, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Menu,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const moderatorPositions = [
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
  { value: 'head', label: 'Head' }
];

const ModeratorManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedModerator, setSelectedModerator] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuModerator, setMenuModerator] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    position: 'junior',
    specialization: '',
    displayName: '',
    bio: ''
  });
  
  const [editForm, setEditForm] = useState({
    position: '',
    specialization: ''
  });

  const fetchModerators = useCallback(async () => {
    try {
      setLoading(true);
      const adminController = new CONTROLLERS.AdminController(api);

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'moderators',
        params: { take: 'all' },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 5 * 60 * 1000 });
      if (Array.isArray(cached?.data)) {
        setModerators(cached.data);
      }

      const response = await adminController.getAllModerators();
      setModerators(response);

      await SERVICES.cacheAdminList(cacheKey, { data: response }, { scope: 'moderators' });
    } catch (error) {
      console.error('Error fetching moderators:', error);
      setSnackbar({ open: true, message: 'Failed to fetch moderators', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [api, auth?.user?.id]);

  const handleCreateModerator = async () => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.createModerator(createForm);
      setSnackbar({ open: true, message: 'Moderator created successfully', severity: 'success' });
      setOpenCreateDialog(false);
      setCreateForm({
        username: '',
        email: '',
        password: '',
        position: 'junior',
        specialization: '',
        displayName: '',
        bio: ''
      });
      fetchModerators();
    } catch (error) {
      console.error('Error creating moderator:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create moderator', severity: 'error' });
    }
  };

  const handleUpdateModerator = async () => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.updateModeratorPosition(selectedModerator.id, editForm);
      setSnackbar({ open: true, message: 'Moderator updated successfully', severity: 'success' });
      setOpenEditDialog(false);
      fetchModerators();
    } catch (error) {
      console.error('Error updating moderator:', error);
      setSnackbar({ open: true, message: 'Failed to update moderator', severity: 'error' });
    }
  };

  const handleMenuOpen = (event, moderator) => {
    setAnchorEl(event.currentTarget);
    setMenuModerator(moderator);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuModerator(null);
  };

  const handleEditClick = (moderator) => {
    setSelectedModerator(moderator);
    setEditForm({ 
      position: moderator.position,
      specialization: moderator.specialization || ''
    });
    setOpenEditDialog(true);
    handleMenuClose();
  };

  useEffect(() => {
    fetchModerators();
  }, [fetchModerators]);

  const getPositionColor = (position) => {
    const colors = {
      head: 'error',
      senior: 'warning',
      junior: 'info'
    };
    return colors[position] || 'default';
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600} mb={1}>Moderator Management</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Manage content moderators and their responsibilities
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={{ borderRadius: 2 }}
          >
            Create Moderator
          </Button>
        </Box>

        {/* Stats Card */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'info.light', mr: 2 }}>
                    <ShieldIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{moderators.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Moderators</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Moderators Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Moderator</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && moderators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : moderators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">No moderators found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                moderators.map((moderator) => (
                  <TableRow key={moderator.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }} src={moderator.getAvatar ? moderator.getAvatar() : null}>
                          {(moderator.getDisplayName ? moderator.getDisplayName() : moderator.username)?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {moderator.getDisplayName ? moderator.getDisplayName() : moderator.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{moderator.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{moderator.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={moderatorPositions.find(p => p.value === moderator.position)?.label || moderator.position}
                        color={getPositionColor(moderator.position)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {moderator.specialization || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {moderator.totalModerationsPerformed || 0} moderations
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {moderator.createdAt ? new Date(moderator.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, moderator)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleEditClick(menuModerator)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Moderator
          </MenuItem>
        </Menu>

        {/* Create Moderator Dialog */}
        <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Moderator</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Username"
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={createForm.position}
                  onChange={(e) => setCreateForm({ ...createForm, position: e.target.value })}
                  label="Position"
                >
                  {moderatorPositions.map((pos) => (
                    <MenuItem key={pos.value} value={pos.value}>{pos.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Specialization"
                value={createForm.specialization}
                onChange={(e) => setCreateForm({ ...createForm, specialization: e.target.value })}
                fullWidth
                placeholder="e.g., content, user_reports, marketplace"
              />
              <TextField
                label="Display Name"
                value={createForm.displayName}
                onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Bio"
                value={createForm.bio}
                onChange={(e) => setCreateForm({ ...createForm, bio: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateModerator} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Moderator Dialog */}
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Moderator</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={editForm.position}
                  onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                  label="Position"
                >
                  {moderatorPositions.map((pos) => (
                    <MenuItem key={pos.value} value={pos.value}>{pos.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Specialization"
                value={editForm.specialization}
                onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                fullWidth
                placeholder="e.g., content, user_reports, marketplace"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateModerator} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default ModeratorManagement;
